import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { api, formatApiErrorDetail, viewFile } from "@/lib/api";
import { toast } from "sonner";
import {
  Save, LogOut, ShieldCheck, ScrollText, ArrowLeft, Upload, Trash2, Eye,
  Check, AlertTriangle, Phone, Camera, UserCog, Sun, Moon,
} from "lucide-react";
import { COMPLIANCE_DOC_TYPES } from "@/lib/compliance";
import { DateTimePicker } from "@/components/ui/date-time-picker";

export default function Settings() {
  const { user, logout } = useAuth();
  const isDriver = user.role === "driver";
  const [view, setView] = useState(null); // null | "compliance" | "cor"
  const [data, setData] = useState({
    name: user.name || "",
    email: user.email || "",
    business_name: user.business_name || "",
    phone: user.phone || "",
    vehicle_type: user.vehicle_type || "",
    registration: user.registration || "",
    abn_acn: user.abn_acn || "",
    password: "",
  });
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { name: data.name, email: data.email, business_name: data.business_name, phone: data.phone };
      if (isDriver) {
        payload.vehicle_type = data.vehicle_type;
        payload.registration = data.registration;
        payload.abn_acn = data.abn_acn;
      }
      if (data.password) payload.password = data.password;
      await api.put("/auth/me", payload);
      toast.success("Profile updated");
      setData({ ...data, password: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (view === "compliance") return <SafetyComplianceView onBack={() => setView(null)} />;
  if (view === "cor") return <ChainOfResponsibilityView onBack={() => setView(null)} />;

  const profileForm = (
    <form onSubmit={save} className="rounded-2xl p-6 border border-white/10 bg-white/[0.02]">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Name">
          <input className="df-input-rect" value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} required data-testid="setting-name" />
        </Field>
        <Field label="Email">
          <input className="df-input-rect" type="email" value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} required data-testid="setting-email" />
        </Field>
        <Field label="Business Name">
          <input className="df-input-rect" value={data.business_name} onChange={(e) => setData({ ...data, business_name: e.target.value })} />
        </Field>
        <Field label="Phone">
          <input className="df-input-rect" value={data.phone} onChange={(e) => setData({ ...data, phone: e.target.value })} />
        </Field>
        {isDriver && (
          <>
            <Field label="Vehicle Type">
              <input className="df-input-rect" value={data.vehicle_type} onChange={(e) => setData({ ...data, vehicle_type: e.target.value })} placeholder="e.g. 1 Tonne Van" data-testid="setting-vehicle-type" />
            </Field>
            <Field label="Registration">
              <input className="df-input-rect" value={data.registration} onChange={(e) => setData({ ...data, registration: e.target.value })} placeholder="e.g. ABC123" data-testid="setting-registration" />
            </Field>
            <Field label="ABN / ACN">
              <input className="df-input-rect" value={data.abn_acn} onChange={(e) => setData({ ...data, abn_acn: e.target.value })} placeholder="e.g. 12 345 678 901" data-testid="setting-abn-acn" />
            </Field>
          </>
        )}
        <Field label="Role">
          <input className="df-input-rect" disabled value={user.role.toUpperCase()} />
        </Field>
        <Field label="New Password (optional)">
          <input className="df-input-rect" type="password" placeholder="Leave blank to keep current" value={data.password} onChange={(e) => setData({ ...data, password: e.target.value })} data-testid="setting-password" />
        </Field>
      </div>
      <div className="mt-6 flex justify-between">
        <button type="button" onClick={logout} className="px-5 py-2.5 rounded-lg text-white font-semibold flex items-center gap-2" style={{ background: "#dc2626" }} data-testid="settings-logout">
          <LogOut size={14} /> Logout
        </button>
        <button type="submit" disabled={busy} className="px-6 py-2.5 rounded-lg text-white font-semibold flex items-center gap-2" style={{ background: "linear-gradient(90deg, #2b5fff, #3b82f6)" }} data-testid="save-settings">
          <Save size={14} /> {busy ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </form>
  );

  // Non-driver (admin/client): the profile form is the whole Settings page.
  if (!isDriver) {
    return (
      <div className="max-w-2xl space-y-5" data-testid="settings-page">
        <div>
          <h1 className="brand-title text-2xl md:text-3xl mb-1">Settings</h1>
          <p className="text-white/60 text-sm">Manage your profile and appearance.</p>
        </div>
        {profileForm}
        <AppearanceCard />
      </div>
    );
  }

  // Driver: profile opens in its own view, just like the other sections.
  if (view === "profile") {
    return (
      <div className="max-w-2xl fade-up" data-testid="settings-page">
        <BackHeader onBack={() => setView(null)} title="Driver Profile" subtitle="Manage your details." />
        {profileForm}
      </div>
    );
  }

  // Driver landing: a list of pills.
  return (
    <div className="max-w-2xl" data-testid="settings-page">
      <h1 className="brand-title text-2xl md:text-3xl mb-1">Settings</h1>
      <p className="text-white/60 text-sm mb-6">Manage your profile, documents and safety.</p>

      <div className="grid sm:grid-cols-2 gap-3">
        <PillButton onClick={() => setView("profile")} icon={<UserCog size={20} />} tint="rgba(59,130,246,0.14)" color="#60a5fa"
          title="Driver Profile" subtitle="Name, contact, vehicle & ABN details" testid="open-profile" />
        <PillButton onClick={() => setView("compliance")} icon={<ShieldCheck size={20} />} tint="rgba(25,230,200,0.12)" color="#19e6c8"
          title="Safety & Compliance" subtitle="Upload your licences, insurance & certificates" testid="open-compliance" />
        <PillButton onClick={() => setView("cor")} icon={<ScrollText size={20} />} tint="rgba(245,158,11,0.12)" color="#f59e0b"
          title="Chain of Responsibility" subtitle="Incident report process & form" testid="open-cor" />
      </div>

      <div className="mt-5">
        <AppearanceCard />
      </div>
    </div>
  );
}

function PillButton({ onClick, icon, tint, color, title, subtitle, testid }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-3 px-5 py-4 rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition-colors text-left"
      data-testid={testid}
    >
      <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: tint, color }}>
        {icon}
      </span>
      <span>
        <span className="block font-semibold text-white">{title}</span>
        <span className="block text-xs text-white/50">{subtitle}</span>
      </span>
    </button>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="block text-white/85 font-semibold text-sm">{label}</label>
    {children}
  </div>
);

// Dark / Light appearance control for the dashboard content (sidebar stays dark).
function AppearanceCard() {
  const { theme, setTheme } = useTheme();
  const opts = [
    { key: "dark", label: "Dark", icon: Moon },
    { key: "light", label: "Light", icon: Sun },
  ];
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="appearance-settings">
      <h2 className="brand-title text-lg mb-1">Appearance</h2>
      <p className="text-white/55 text-sm mb-4">Switch the dashboard between dark and light. The sidebar and top bar stay dark.</p>
      <div className="inline-flex rounded-xl border border-white/10 bg-white/[0.03] p-1 gap-1">
        {opts.map((o) => {
          const Icon = o.icon;
          const active = theme === o.key;
          return (
            <button
              key={o.key}
              type="button"
              onClick={() => setTheme(o.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${active ? "text-white" : "text-white/55 hover:text-white"}`}
              style={active ? { background: "linear-gradient(90deg,#2b5fff,#3b82f6)" } : {}}
              data-testid={`appearance-${o.key}`}
            >
              <Icon size={15} /> {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BackHeader({ onBack, title, subtitle }) {
  return (
    <div className="mb-6">
      <button onClick={onBack} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/[0.06] hover:bg-white/10 text-white/70 hover:text-white text-sm font-semibold transition-colors mb-4">
        <ArrowLeft size={16} /> Back
      </button>
      <h1 className="brand-title text-2xl md:text-3xl">{title}</h1>
      {subtitle && <p className="text-white/55 text-sm mt-1">{subtitle}</p>}
    </div>
  );
}

// ── Safety & Compliance ───────────────────────────────────────────────────────
function SafetyComplianceView({ onBack }) {
  const [docs, setDocs] = useState({});
  const [busyKey, setBusyKey] = useState(null);
  const inputs = useRef({});

  const load = async () => {
    try {
      const { data } = await api.get("/driver/compliance-docs");
      setDocs(data.docs || {});
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (key, file) => {
    if (!file) return;
    setBusyKey(key);
    try {
      const fd = new FormData();
      fd.append("doc_type", key);
      fd.append("file", file);
      const { data } = await api.post("/driver/compliance-docs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs(data.docs || {});
      toast.success("Document uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusyKey(null);
      if (inputs.current[key]) inputs.current[key].value = "";
    }
  };

  const remove = async (key) => {
    if (!window.confirm("Remove this document?")) return;
    setBusyKey(key);
    try {
      const { data } = await api.delete(`/driver/compliance-docs/${key}`);
      setDocs(data.docs || {});
      toast.success("Document removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusyKey(null); }
  };

  const open = async (path) => {
    try { await viewFile(path); } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="max-w-2xl fade-up" data-testid="compliance-page">
      <BackHeader onBack={onBack} title="Safety & Compliance" subtitle="Upload your documents. Accepted: images or PDF (max 20 MB)." />

      <div className="space-y-3">
        {COMPLIANCE_DOC_TYPES.map(({ key, label }) => {
          const doc = docs[key];
          const isBusy = busyKey === key;
          return (
            <div key={key} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 flex flex-wrap items-center gap-3" data-testid={`compliance-${key}`}>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-white text-sm">{label}</p>
                {doc ? (
                  <p className="text-xs text-[#19e6c8] truncate flex items-center gap-1.5 mt-0.5">
                    <Check size={12} /> {doc.name}
                  </p>
                ) : (
                  <p className="text-xs text-white/40 mt-0.5">Not uploaded</p>
                )}
              </div>
              <input
                ref={(el) => (inputs.current[key] = el)}
                type="file"
                accept="image/*,application/pdf"
                hidden
                onChange={(e) => upload(key, e.target.files?.[0])}
                data-testid={`compliance-input-${key}`}
              />
              {doc && (
                <button onClick={() => open(doc.path)} className="px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1" style={{ background: "rgba(59,130,246,0.15)", color: "#60a5fa" }}>
                  <Eye size={13} /> View
                </button>
              )}
              <button
                onClick={() => inputs.current[key]?.click()}
                disabled={isBusy}
                className="px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1 disabled:opacity-60"
                style={{ background: "rgba(25,230,200,0.12)", color: "#19e6c8" }}
                data-testid={`compliance-upload-${key}`}>
                <Upload size={13} /> {isBusy ? "..." : doc ? "Replace" : "Upload"}
              </button>
              {doc && (
                <button onClick={() => remove(key)} disabled={isBusy} className="px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1 disabled:opacity-60" style={{ background: "rgba(239,68,68,0.12)", color: "#ef4444" }}>
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Chain of Responsibility ───────────────────────────────────────────────────
const INCIDENT_TYPES = ["Accident", "Cargo damage", "Injury", "Near miss", "Other"];

function ChainOfResponsibilityView({ onBack }) {
  const [form, setForm] = useState({
    occurred_at: "",
    location: "",
    incident_type: "",
    description: "",
    people_involved: "",
    witnesses: "",
    injuries_damage: "",
    emergency_called: "No",
    emergency_ref: "",
  });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPick = (e) => {
    const sel = Array.from(e.target.files || []).slice(0, 8 - files.length);
    setFiles((f) => [...f, ...sel].slice(0, 8));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) { toast.error("Please describe what happened"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
      files.forEach((f) => fd.append("files", f));
      await api.post("/driver/incident-report", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Incident report submitted — a copy was emailed to operations.");
      setForm({
        occurred_at: "", location: "", incident_type: "", description: "",
        people_involved: "", witnesses: "", injuries_damage: "", emergency_called: "No", emergency_ref: "",
      });
      setFiles([]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl fade-up" data-testid="cor-page">
      <BackHeader onBack={onBack} title="Chain of Responsibility" />

      {/* Required: Incident Report Process */}
      <div className="rounded-2xl border p-5 mb-5" style={{ background: "rgba(245,158,11,0.06)", borderColor: "rgba(245,158,11,0.25)" }}>
        <p className="flex items-center gap-2 font-semibold mb-3" style={{ color: "#f59e0b" }}>
          <AlertTriangle size={18} /> REQUIRED: Incident Report Process
        </p>
        <p className="text-sm text-white/80 leading-relaxed">
          Hi Team, if you are involved in any incident (accident, cargo damage, injury, near miss), you
          must fill out the Incident Report Form below immediately at the scene if safe to do so. Please
          take photos of the scene / damage. Email the completed report to{" "}
          <a href="mailto:hello@drivnfreight.com.au" className="text-[#19e6c8] underline decoration-dotted">hello@drivnfreight.com.au</a>{" "}
          within 24 hrs. If the incident is serious, please call Nithin at{" "}
          <a href="tel:0470344648" className="text-[#19e6c8] underline decoration-dotted whitespace-nowrap">0470 344 648</a>{" "}
          immediately before filling the report.
        </p>
        <p className="text-sm mt-3 flex items-center gap-2 font-semibold text-white/80">
          <Phone size={15} className="text-red-500" />
          Call <span style={{ color: "#ef4444" }} className="font-bold">000</span> for Emergency Assistance.
        </p>
      </div>

      {/* Incident Report Form */}
      <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <h2 className="brand-title text-lg mb-4 flex items-center gap-2">
          <ScrollText size={18} className="text-[#f59e0b]" /> Incident Report Form
        </h2>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Date & time of incident">
            <DateTimePicker value={form.occurred_at} onChange={(v) => set("occurred_at", v)} data-testid="incident-datetime" />
          </Field>
          <Field label="Location">
            <input className="df-input-rect" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Where did it happen?" data-testid="incident-location" />
          </Field>
          <Field label="Incident type">
            <select className="df-input-rect" value={form.incident_type} onChange={(e) => set("incident_type", e.target.value)} data-testid="incident-type">
              <option value="" className="bg-[#15151f]">Select…</option>
              {INCIDENT_TYPES.map((t) => <option key={t} value={t} className="bg-[#15151f]">{t}</option>)}
            </select>
          </Field>
          <Field label="Emergency services called?">
            <select className="df-input-rect" value={form.emergency_called} onChange={(e) => set("emergency_called", e.target.value)} data-testid="incident-emergency">
              <option value="No" className="bg-[#15151f]">No</option>
              <option value="Yes" className="bg-[#15151f]">Yes</option>
            </select>
          </Field>
        </div>

        <div className="mt-4">
          <Field label="What happened? (description)">
            <textarea rows={4} className="df-input-rect resize-none" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Describe the incident in detail…" required data-testid="incident-description" />
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <Field label="People involved">
            <input className="df-input-rect" value={form.people_involved} onChange={(e) => set("people_involved", e.target.value)} placeholder="Names / parties" data-testid="incident-people" />
          </Field>
          <Field label="Witnesses">
            <input className="df-input-rect" value={form.witnesses} onChange={(e) => set("witnesses", e.target.value)} placeholder="Names / contact" data-testid="incident-witnesses" />
          </Field>
          <Field label="Injuries / damage">
            <input className="df-input-rect" value={form.injuries_damage} onChange={(e) => set("injuries_damage", e.target.value)} placeholder="Any injuries or damage" data-testid="incident-injuries" />
          </Field>
          <Field label="Police / Emergency reference">
            <input className="df-input-rect" value={form.emergency_ref} onChange={(e) => set("emergency_ref", e.target.value)} placeholder="Reference no. (if any)" data-testid="incident-ref" />
          </Field>
        </div>

        {/* Photos */}
        <div className="mt-4">
          <label className="block text-white/85 font-semibold text-sm mb-2">Photos of scene / damage ({files.length}/8)</label>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
            {files.map((f, i) => (
              <div key={i} className="relative aspect-square">
                <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover rounded-xl border border-white/10" />
                <button type="button" onClick={() => setFiles((arr) => arr.filter((_, idx) => idx !== i))} className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-red-500 text-white text-xs flex items-center justify-center shadow-lg">×</button>
              </div>
            ))}
            {files.length < 8 && (
              <label className="aspect-square rounded-xl border-2 border-dashed border-white/25 flex flex-col items-center justify-center text-white/50 cursor-pointer hover:border-amber-400/60 hover:text-amber-400/60 transition-colors">
                <Camera size={20} /><span className="text-[10px] mt-1 font-semibold">Add</span>
                <input type="file" accept="image/*" capture="environment" multiple hidden onChange={onPick} data-testid="incident-photos" />
              </label>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button type="submit" disabled={busy} className="px-6 py-2.5 rounded-lg text-white font-semibold flex items-center gap-2 disabled:opacity-60" style={{ background: "linear-gradient(90deg, #f59e0b, #facc15)", color: "#1a1a2e" }} data-testid="submit-incident">
            <Check size={16} /> {busy ? "Submitting…" : "Submit Incident Report"}
          </button>
        </div>
      </form>
    </div>
  );
}
